import React, { useEffect, useState } from 'react';
import axiosInstance from '../../AxiosInstance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faFileImport, faSearch, faFilePdf, faFile, faDownload, faTimes, faImage } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../components/modal/modal';
import { TextInput } from '../../components/input/inputs';
import PartsImportModal from './components/parts_import_modal';
import {useTranslation} from "react-i18next";
import PaginationControls, {getStoredPageSize} from "../../components/pagination/PaginationControls";

function StockBadge({ stockHere, stockByMachine, machine }) {
    const {t} = useTranslation();
    const others = (stockByMachine || []).filter(s => s.machine_id !== machine.id);
    const othersTotal = others.reduce((sum, s) => sum + s.quantity, 0);

    const colorHere = stockHere > 0 ? '#16A34A' : (othersTotal > 0 ? '#D97706' : '#DC2626');
    const bgHere = stockHere > 0 ? '#F0FDF4' : (othersTotal > 0 ? '#FFFBEB' : '#FEF2F2');

    return (
        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '12px' }}>
            <span style={{ padding: '2px 8px', borderRadius: '4px', background: bgHere, color: colorHere, fontWeight: 600 }}>
                {t('maintenance.catalog.stockHere', {count: stockHere})}
            </span>
            {othersTotal > 0 && (
                <span title={others.map(s => `${s.machine_name}: ${s.quantity}`).join('\n')}
                    style={{ padding: '2px 8px', borderRadius: '4px', background: '#EFF6FF', color: '#2563EB', cursor: 'help' }}>
                    {t('maintenance.catalog.stockOthers', {count: othersTotal})}
                </span>
            )}
            <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#F1F5F9', color: '#475569', fontWeight: 600 }}>
                {t('maintenance.catalog.stockTotal', {count: stockHere + othersTotal})}
            </span>
        </div>
    );
}

export default function MaintenancePartsTab({ machine }) {
    const {t} = useTranslation();
    const project_id = parseInt(localStorage.getItem('project_id'));
    const [subTab, setSubTab] = useState('catalog');
    const [parts, setParts] = useState([]);
    const [mostUsed, setMostUsed] = useState([]);
    const [search, setSearch] = useState('');
    const [catalogPage, setCatalogPage] = useState(1);
    const [catalogPageSize, setCatalogPageSize] = useState(getStoredPageSize);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPageSize, setHistoryPageSize] = useState(getStoredPageSize);
    const [viewMode, setViewMode] = useState('machine'); // 'machine' | 'project'
    const [showModal, setShowModal] = useState(false);
    const [editPart, setEditPart] = useState(null);
    const [form, setForm] = useState({});
    const [imageFile, setImageFile] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePart, setDeletePart] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [docFiles, setDocFiles] = useState([]); // pending uploads for create/edit
    const [deletingDocId, setDeletingDocId] = useState(null);

    const fetchParts = () => {
        const params = viewMode === 'machine'
            ? `project_id=${project_id}&machine_id=${machine.id}`
            : `project_id=${project_id}`;
        axiosInstance.get(`spare_part/?${params}`).then(res => setParts(res.data));
    };

    const fetchMostUsed = () => {
        axiosInstance.get(`spare_part/most_used/?machine_id=${machine.id}`).then(res => setMostUsed(res.data));
    };

    useEffect(() => {
        fetchParts();
        fetchMostUsed();
    }, [machine.id, viewMode]);

    useEffect(() => {
        setCatalogPage(1);
    }, [search]);

    const openCreate = () => {
        setEditPart(null);
        setForm({ project: project_id, machine_id: machine.id, initial_quantity: 0 });
        setImageFile(null);
        setDocFiles([]);
        setShowModal(true);
    };

    const openEdit = (part) => {
        setEditPart(part);
        setForm({ ...part });
        setImageFile(null);
        setDocFiles([]);
        setShowModal(true);
    };

    const removeDocFile = (index) => {
        setDocFiles(prev => prev.filter((_, i) => i !== index));
    };

    const savePart = () => {
        const formData = new FormData();
        Object.entries(form).forEach(([k, v]) => { if (v !== undefined && v !== null) formData.append(k, v); });
        if (imageFile) formData.append('image', imageFile);
        const req = editPart
            ? axiosInstance.put(`spare_part/${editPart.id}/`, formData)
            : axiosInstance.post('spare_part/', formData);
        req.then((res) => {
            const partId = res.data.id;
            // Upload any pending document files
            const uploads = docFiles.map(f => {
                const fd = new FormData();
                fd.append('spare_part', partId);
                fd.append('file', f);
                fd.append('name', f.name);
                return axiosInstance.post('spare_part_document/', fd);
            });
            Promise.all(uploads).then(() => { setShowModal(false); fetchParts(); });
        });
    };

    const deleteDocument = (docId) => {
        setDeletingDocId(docId);
        axiosInstance.delete(`spare_part_document/${docId}/`).then(() => {
            setDeletingDocId(null);
            fetchParts();
            if (editPart) {
                setForm(prev => ({
                    ...prev,
                    documents: (prev.documents || []).filter(d => d.id !== docId)
                }));
            }
        }).catch(() => setDeletingDocId(null));
    };

    const confirmDelete = () => {
        axiosInstance.delete(`spare_part/${deletePart.id}/`).then(() => {
            setShowDeleteModal(false);
            fetchParts();
        });
    };

    const updateStock = (stockEntryId, partId, qty) => {
        if (stockEntryId) {
            axiosInstance.put(`spare_part_stock/${stockEntryId}/`, { quantity: parseInt(qty) || 0 }).then(fetchParts);
        } else {
            axiosInstance.post('spare_part_stock/', { spare_part: partId, machine: machine.id, quantity: parseInt(qty) || 0 }).then(fetchParts);
        }
    };

    const filtered = parts.filter(p =>
        !search || p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.brand || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.part_number || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div>
            {/* Sub-tabs */}
            <div className="d-flex gap-2 mb-3">
                {[['catalog', t('maintenance.catalog.catalog')], ['history', t('maintenance.catalog.usageHistory')]].map(([id, label]) => (
                    <button
                        key={id}
                        onClick={() => setSubTab(id)}
                        style={{
                            padding: '6px 14px', borderRadius: '6px', border: '1px solid',
                            borderColor: subTab === id ? '#4F46E5' : '#E2E8F0',
                            background: subTab === id ? '#4F46E5' : '#FFF',
                            color: subTab === id ? '#FFF' : '#64748B',
                            fontWeight: 500, fontSize: '13px', cursor: 'pointer',
                        }}
                    >{label}</button>
                ))}
            </div>

            {subTab === 'catalog' && (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div className="d-flex gap-2 align-items-center">
                            {/* View toggle */}
                            <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #E2E8F0' }}>
                                {[['machine', t('maintenance.catalog.thisMachine')], ['project', t('maintenance.catalog.allProject')]].map(([v, label]) => (
                                    <button key={v} onClick={() => setViewMode(v)} style={{
                                        padding: '5px 12px', border: 'none', cursor: 'pointer', fontSize: '13px',
                                        background: viewMode === v ? '#4F46E5' : '#FFF',
                                        color: viewMode === v ? '#FFF' : '#64748B',
                                    }}>{label}</button>
                                ))}
                            </div>
                            {/* Search */}
                            <div style={{ position: 'relative' }}>
                                <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '13px' }} />
                                <input
                                    placeholder="Search..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    style={{ padding: '6px 10px 6px 30px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', width: '180px' }}
                                />
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <button onClick={() => setShowImportModal(true)} style={{
                                padding: '7px 14px', borderRadius: '6px', border: '1px solid #E2E8F0',
                                background: '#FFF', color: '#64748B', fontSize: '13px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '5px',
                            }}>
                                <FontAwesomeIcon icon={faFileImport} /> {t('maintenance.importParts')}
                            </button>
                            <button onClick={openCreate} style={{
                                padding: '7px 14px', borderRadius: '6px', border: 'none',
                                background: '#4F46E5', color: '#FFF', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '5px',
                            }}>
                                <FontAwesomeIcon icon={faPlus} /> {t('maintenance.newPart')}
                            </button>
                        </div>
                    </div>

                    {filtered.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '48px', color: '#94A3B8' }}>{t('maintenance.catalog.noParts')}</div>
                    ) : (() => {
                        const catalogTotalPages = Math.ceil(filtered.length / catalogPageSize);
                        const pagedFiltered = filtered.slice((catalogPage - 1) * catalogPageSize, catalogPage * catalogPageSize);
                        return (
                        <div style={{ background: '#FFF', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'auto', paddingBottom: '12px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#F8FAFC' }}>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.records.part')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.catalog.brandModel')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.records.pn')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.catalog.stock')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.catalog.uses')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedFiltered.map(part => {
                                        const myStock = (part.stock_by_machine || []).find(s => s.machine_id === machine.id);
                                        const stockHere = myStock?.quantity ?? 0;
                                        return (
                                            <tr key={part.id} style={{ borderTop: '1px solid #F1F5F9' }}>
                                                <td style={{ padding: '10px 14px' }}>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {part.image && <img src={part.image} alt="" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} />}
                                                        <div>
                                                            <div style={{ fontWeight: 500, color: '#1E293B' }}>{part.name}</div>
                                                            {part.description && <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '1px' }}>{part.description.slice(0, 50)}{part.description.length > 50 ? '…' : ''}</div>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '10px 14px', color: '#64748B' }}>{[part.brand, part.model].filter(Boolean).join(' / ') || '—'}</td>
                                                <td style={{ padding: '10px 14px', color: '#64748B', fontFamily: 'monospace' }}>{part.part_number || '—'}</td>
                                                <td style={{ padding: '10px 14px' }}>
                                                    {viewMode === 'machine' ? (
                                                        <div className="d-flex align-items-center gap-2">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                defaultValue={stockHere}
                                                                onBlur={e => updateStock(myStock?.stock_entry_id, part.id, e.target.value)}
                                                                style={{ width: '60px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #E2E8F0', fontSize: '13px', textAlign: 'center' }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <StockBadge stockHere={stockHere} stockByMachine={part.stock_by_machine} machine={machine} />
                                                    )}
                                                </td>
                                                <td style={{ padding: '10px 14px', color: '#334155', fontWeight: 500 }}>{part.times_used}</td>
                                                <td style={{ padding: '10px 14px' }}>
                                                    <div className="d-flex gap-1">
                                                        <button onClick={() => openEdit(part)} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', padding: '4px' }}>
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                        <button onClick={() => { setDeletePart(part); setShowDeleteModal(true); }} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', padding: '4px' }}>
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div style={{ marginTop: '10px', marginLeft: '6px' }}>
                                <PaginationControls
                                    page={catalogPage}
                                    totalPages={catalogTotalPages}
                                    pageSize={catalogPageSize}
                                    onPageChange={setCatalogPage}
                                    onPageSizeChange={(newSize) => { setCatalogPageSize(newSize); setCatalogPage(1); }}
                                    totalCount={filtered.length}
                                />
                            </div>
                        </div>
                        );
                    })()}
                </>
            )}

            {subTab === 'history' && (() => {
                const historyTotalPages = Math.ceil(mostUsed.length / historyPageSize);
                const pagedMostUsed = mostUsed.slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize);
                return (
                <div style={{ background: '#FFF', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'auto', paddingBottom: '12px' }}>
                    {mostUsed.length === 0 ? (
                        <div style={{ padding: '48px', textAlign: 'center', color: '#94A3B8' }}>{t('maintenance.catalog.noHistory')}</div>
                    ) : (
                        <>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#F8FAFC' }}>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.records.part')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.records.brand')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748B', fontWeight: 500 }}>{t('maintenance.records.pn')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'right', color: '#64748B', fontWeight: 500 }}>{t('maintenance.catalog.timesUsed')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'right', color: '#64748B', fontWeight: 500 }}>{t('maintenance.catalog.totalQty')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedMostUsed.map((p, i) => (
                                        <tr key={p.spare_part} style={{ borderTop: '1px solid #F1F5F9' }}>
                                            <td style={{ padding: '10px 14px', fontWeight: 500, color: '#1E293B' }}>
                                                {i === 0 && <span style={{ marginRight: '6px' }}>🔴</span>}
                                                {i === 1 && <span style={{ marginRight: '6px' }}>🟠</span>}
                                                {i === 2 && <span style={{ marginRight: '6px' }}>🟡</span>}
                                                {p['spare_part__name']}
                                            </td>
                                            <td style={{ padding: '10px 14px', color: '#64748B' }}>{p['spare_part__brand'] || '—'}</td>
                                            <td style={{ padding: '10px 14px', color: '#64748B', fontFamily: 'monospace' }}>{p['spare_part__part_number'] || '—'}</td>
                                            <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: '#4F46E5' }}>{p.times_used}</td>
                                            <td style={{ padding: '10px 14px', textAlign: 'right', color: '#334155' }}>{p.total_quantity}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div style={{ marginTop: '10px', marginLeft: '6px' }}>
                                <PaginationControls
                                    page={historyPage}
                                    totalPages={historyTotalPages}
                                    pageSize={historyPageSize}
                                    onPageChange={setHistoryPage}
                                    onPageSizeChange={(newSize) => { setHistoryPageSize(newSize); setHistoryPage(1); }}
                                    totalCount={mostUsed.length}
                                />
                            </div>
                        </>
                    )}
                </div>
                );
            })()}

            {/* Create/Edit modal */}
            <Modal show={showModal} setter={setShowModal} name={editPart ? t('maintenance.editPart') : t('maintenance.newPart')} buttonName={t('common.save')} onClick={savePart}>
                <TextInput form={form} setForm={setForm} name={t('maintenance.plans.name')} internalName="name" />
                <TextInput form={form} setForm={setForm} name={t('maintenance.records.brand')} internalName="brand" />
                <TextInput form={form} setForm={setForm} name={t('maintenance.catalog.model')} internalName="model" />
                <TextInput form={form} setForm={setForm} name={t('maintenance.catalog.partNumberPn')} internalName="part_number" />
                <TextInput form={form} setForm={setForm} name={t('maintenance.catalog.descriptionSpecs')} internalName="description" />
                {!editPart && <TextInput form={form} setForm={setForm} name={t('maintenance.catalog.initialQty')} internalName="initial_quantity" />}
                <div style={{ marginTop: '8px' }}>
                    <label style={{ fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '6px' }}>{t('maintenance.catalog.partImage')}</label>
                    {/* Show existing image in edit mode */}
                    {editPart && form.image && !imageFile && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#F8FAFC', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                <img src={form.image} alt="" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                                <span style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#475569' }}>
                                    {form.image.split('/').pop()}
                                </span>
                            </div>
                            <FontAwesomeIcon
                                icon={faTimes}
                                color="#94A3B8"
                                style={{ cursor: 'pointer', flexShrink: 0, marginLeft: '8px' }}
                                onClick={() => setForm(f => ({ ...f, image: null }))}
                            />
                        </div>
                    )}
                    {/* Show new image selected */}
                    {imageFile && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#F8FAFC', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                <img src={URL.createObjectURL(imageFile)} alt="" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                                <span style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#475569' }}>{imageFile.name}</span>
                            </div>
                            <FontAwesomeIcon
                                icon={faTimes}
                                color="#94A3B8"
                                style={{ cursor: 'pointer', flexShrink: 0, marginLeft: '8px' }}
                                onClick={() => setImageFile(null)}
                            />
                        </div>
                    )}
                    {/* Upload button — only show if no image yet */}
                    {!imageFile && !(editPart && form.image) && (
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '6px', border: '1px dashed #94A3B8', background: '#F8FAFC', color: '#475569', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}>
                            <FontAwesomeIcon icon={faImage} size="xs" />
                            {t('maintenance.catalog.partImage')}
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setImageFile(e.target.files[0])} />
                        </label>
                    )}
                    {/* Replace button — show when an image exists */}
                    {(imageFile || (editPart && form.image)) && (
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '6px', border: '1px dashed #94A3B8', background: '#F8FAFC', color: '#94A3B8', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}>
                            <FontAwesomeIcon icon={faImage} size="xs" />
                            {t('common.change') || 'Cambiar'}
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setImageFile(e.target.files[0])} />
                        </label>
                    )}
                </div>
                {/* Existing documents (edit mode) */}
                {editPart && (form.documents || []).length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                        <label style={{ fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '6px' }}>{t('maintenance.catalog.uploadedDocs')}</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {(form.documents || []).map(doc => (
                                <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', overflow: 'hidden' }}>
                                        <FontAwesomeIcon icon={doc.file?.endsWith('.pdf') ? faFilePdf : faFile} color="#64748B" />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                        <a href={doc.file} target="_blank" rel="noreferrer" style={{ color: '#4F46E5' }}>
                                            <FontAwesomeIcon icon={faDownload} />
                                        </a>
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            color={deletingDocId === doc.id ? '#94A3B8' : '#DC2626'}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => deleteDocument(doc.id)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {/* Upload new documents */}
                <div style={{ marginTop: '12px' }}>
                    <label style={{ fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '6px' }}>
                        {t('maintenance.catalog.addDocs')}
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '6px', border: '1px dashed #94A3B8', background: '#F8FAFC', color: '#475569', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}>
                        <FontAwesomeIcon icon={faPlus} size="xs" />
                        {t('maintenance.catalog.addDocs')}
                        <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.png,.jpg,.jpeg"
                            style={{ display: 'none' }}
                            onChange={e => setDocFiles(prev => [...prev, ...Array.from(e.target.files)])}
                        />
                    </label>
                    {docFiles.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                            {docFiles.map((file, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', overflow: 'hidden' }}>
                                        <FontAwesomeIcon icon={file.name.endsWith('.pdf') ? faFilePdf : faFile} color="#64748B" />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                    </div>
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        color="#94A3B8"
                                        style={{ cursor: 'pointer', flexShrink: 0, marginLeft: '8px' }}
                                        onClick={() => removeDocFile(i)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>

            {/* Delete modal */}
            <Modal show={showDeleteModal} setter={setShowDeleteModal} name={t('maintenance.deletePart')} buttonName={t('common.delete')} onClick={confirmDelete} danger>
                <p>{t('maintenance.catalog.confirmDelete', {name: deletePart?.name})}</p>
            </Modal>

            {/* Import modal */}
            {showImportModal && (
                <PartsImportModal
                    show={showImportModal}
                    onClose={() => { setShowImportModal(false); fetchParts(); }}
                    projectId={project_id}
                    machineId={machine.id}
                />
            )}
        </div>
    );
}
